/**
 * Historical Tracking Component
 * Shows how portfolio health metrics have changed over time
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, History, Award, Target } from 'lucide-react';
import { Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { cn } from '@/lib/utils';

interface HealthSnapshot {
  date: Date;
  overallScore: number;
  diversificationScore: number;
  costScore: number;
  riskScore: number;
  taxEfficiencyScore: number;
}

interface HistoricalTrackingProps {
  snapshots: HealthSnapshot[];
  currentScore: number;
  targetScore?: number;
  className?: string;
}

// Generate mock historical data if none provided
const generateMockSnapshots = (currentScore: number): HealthSnapshot[] => {
  const snapshots: HealthSnapshot[] = [];
  const now = new Date();

  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setMonth(date.getMonth() - i);

    // Simulate gradual improvement with some noise
    const progressFactor = 1 - (i / 10);
    const noise = (Math.random() - 0.5) * 10;
    const baseScore = Math.max(40, currentScore - 20 + (20 * progressFactor) + noise);

    snapshots.push({
      date,
      overallScore: Math.round(baseScore),
      diversificationScore: Math.round(baseScore + (Math.random() - 0.5) * 15),
      costScore: Math.round(baseScore + (Math.random() - 0.5) * 15),
      riskScore: Math.round(baseScore + (Math.random() - 0.5) * 15),
      taxEfficiencyScore: Math.round(baseScore + (Math.random() - 0.5) * 15),
    });
  }

  return snapshots;
};

export function HistoricalTracking({
  snapshots: providedSnapshots,
  currentScore,
  targetScore = 85,
  className,
}: HistoricalTrackingProps) {
  // Use provided snapshots or generate mock data
  const snapshots = useMemo(() => {
    return providedSnapshots && providedSnapshots.length > 0
      ? providedSnapshots
      : generateMockSnapshots(currentScore);
  }, [providedSnapshots, currentScore]);

  // Calculate improvement metrics
  const metrics = useMemo(() => {
    if (snapshots.length < 2) {
      return {
        totalImprovement: 0,
        monthlyAvgImprovement: 0,
        streak: 0,
        bestMonth: null,
        worstMonth: null,
        projectedTimeToTarget: null,
      };
    }

    const firstScore = snapshots[0].overallScore;
    const lastScore = snapshots[snapshots.length - 1].overallScore;
    const totalImprovement = lastScore - firstScore;
    const monthlyAvgImprovement = totalImprovement / (snapshots.length - 1);

    // Calculate streak of consecutive improvements
    let streak = 0;
    for (let i = snapshots.length - 1; i > 0; i--) {
      if (snapshots[i].overallScore > snapshots[i - 1].overallScore) {
        streak++;
      } else {
        break;
      }
    }

    // Find best and worst months
    let bestImprovement = -Infinity;
    let worstImprovement = Infinity;
    let bestMonth = null;
    let worstMonth = null;

    for (let i = 1; i < snapshots.length; i++) {
      const improvement = snapshots[i].overallScore - snapshots[i - 1].overallScore;
      if (improvement > bestImprovement) {
        bestImprovement = improvement;
        bestMonth = snapshots[i].date;
      }
      if (improvement < worstImprovement) {
        worstImprovement = improvement;
        worstMonth = snapshots[i].date;
      }
    }

    // Project time to target
    const pointsToTarget = targetScore - lastScore;
    const projectedMonths = monthlyAvgImprovement > 0
      ? Math.ceil(pointsToTarget / monthlyAvgImprovement)
      : null;

    return {
      totalImprovement,
      monthlyAvgImprovement,
      streak,
      bestMonth,
      worstMonth,
      bestImprovement,
      worstImprovement,
      projectedTimeToTarget: projectedMonths,
    };
  }, [snapshots, targetScore]);

  // Format data for chart
  const chartData = useMemo(() => {
    return snapshots.map(s => ({
      date: s.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      score: s.overallScore,
      diversification: s.diversificationScore,
      cost: s.costScore,
      risk: s.riskScore,
      tax: s.taxEfficiencyScore,
      target: targetScore,
    }));
  }, [snapshots, targetScore]);

  const getTrendIcon = (value: number) => {
    if (value > 0) return <TrendingUp className="h-4 w-4 text-status-good" />;
    if (value < 0) return <TrendingDown className="h-4 w-4 text-status-critical" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <Card className={cn("col-span-full", className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            Portfolio Health Over Time
          </CardTitle>
          <Badge variant={metrics.totalImprovement >= 0 ? "default" : "destructive"}>
            {metrics.totalImprovement >= 0 ? '+' : ''}{metrics.totalImprovement} pts overall
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Track your portfolio improvements and see progress toward your goals.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-muted/30 border">
            <div className="flex items-center gap-2">
              {getTrendIcon(metrics.monthlyAvgImprovement)}
              <p className="text-xs text-muted-foreground">Avg Monthly Change</p>
            </div>
            <p className={cn(
              "text-lg font-mono font-bold mt-1",
              metrics.monthlyAvgImprovement > 0 && "text-status-good",
              metrics.monthlyAvgImprovement < 0 && "text-status-critical"
            )}>
              {metrics.monthlyAvgImprovement >= 0 ? '+' : ''}{metrics.monthlyAvgImprovement.toFixed(1)} pts
            </p>
          </div>

          <div className="p-3 rounded-lg bg-muted/30 border">
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-status-good" />
              <p className="text-xs text-muted-foreground">Improvement Streak</p>
            </div>
            <p className="text-lg font-mono font-bold mt-1">
              {metrics.streak} month{metrics.streak !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="p-3 rounded-lg bg-muted/30 border">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Target Score</p>
            </div>
            <p className="text-lg font-mono font-bold mt-1">{targetScore}</p>
            <p className="text-[10px] text-muted-foreground">
              {currentScore >= targetScore ? 'Achieved!' : `${targetScore - currentScore} pts to go`}
            </p>
          </div>

          <div className="p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground">Est. Time to Target</p>
            <p className="text-lg font-mono font-bold mt-1">
              {currentScore >= targetScore
                ? 'Done!'
                : metrics.projectedTimeToTarget
                  ? `~${metrics.projectedTimeToTarget} mo`
                  : 'N/A'}
            </p>
            <p className="text-[10px] text-muted-foreground">
              at current pace
            </p>
          </div>
        </div>

        {/* Main Chart */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Score Trend</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  width={30}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                    fontSize: '12px',
                  }}
                />
                {/* Target line */}
                <Line
                  type="monotone"
                  dataKey="target"
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={1}
                  strokeDasharray="5 5"
                  dot={false}
                />
                {/* Score area */}
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#scoreGradient)"
                  dot={{ r: 3, fill: 'hsl(var(--primary))' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-6 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-primary rounded" />
              <span className="text-muted-foreground">Your Score</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-muted-foreground rounded border-dashed" style={{ borderStyle: 'dashed' }} />
              <span className="text-muted-foreground">Target ({targetScore})</span>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Category Progress</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'diversification', label: 'Diversification', field: 'diversificationScore' },
              { key: 'cost', label: 'Cost Efficiency', field: 'costScore' },
              { key: 'risk', label: 'Risk Management', field: 'riskScore' },
              { key: 'tax', label: 'Tax Efficiency', field: 'taxEfficiencyScore' },
            ].map(category => {
              const latest = snapshots[snapshots.length - 1];
              const first = snapshots[0];
              const change = (latest[category.field as keyof HealthSnapshot] as number) -
                           (first[category.field as keyof HealthSnapshot] as number);

              const latestScore = latest[category.field as keyof HealthSnapshot] as number;

              return (
                <div key={category.key} className="p-3 rounded-lg bg-muted/20 border space-y-2">
                  <p className="text-xs font-medium">{category.label}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-mono font-bold">
                      {latestScore}
                    </span>
                    <span className={cn(
                      "text-xs font-mono",
                      change > 0 && "text-status-good",
                      change < 0 && "text-status-critical"
                    )}>
                      {change >= 0 ? '+' : ''}{change}
                    </span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        latestScore >= 70
                          ? "bg-status-good"
                          : latestScore >= 50
                            ? "bg-status-warning"
                            : "bg-status-critical"
                      )}
                      style={{ width: `${latestScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Milestones */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Milestones</h4>
          <div className="flex flex-wrap gap-2">
            {currentScore >= 50 && (
              <Badge variant="outline" className="gap-1">
                <Award className="h-3 w-3" />
                Foundation (50+)
              </Badge>
            )}
            {currentScore >= 70 && (
              <Badge variant="outline" className="gap-1">
                <Award className="h-3 w-3" />
                Solid (70+)
              </Badge>
            )}
            {currentScore >= 85 && (
              <Badge variant="default" className="gap-1">
                <Award className="h-3 w-3" />
                Excellent (85+)
              </Badge>
            )}
            {metrics.streak >= 3 && (
              <Badge variant="secondary" className="gap-1">
                <TrendingUp className="h-3 w-3" />
                {metrics.streak}-Month Streak
              </Badge>
            )}
          </div>
        </div>

        {/* Tips */}
        {currentScore < targetScore && (
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/30 text-xs">
            <p className="font-medium text-primary mb-1">Keep Going!</p>
            <p className="text-muted-foreground">
              You're making progress! Focus on your lowest-scoring category to see the biggest improvements.
              {metrics.projectedTimeToTarget && metrics.projectedTimeToTarget > 0 && (
                <span className="block mt-1">
                  At your current pace, you'll reach your target in approximately {metrics.projectedTimeToTarget} months.
                </span>
              )}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
